import React, { useContext, useEffect, useRef, useState } from 'react';
import { Box, Button, Typography, TextField, Checkbox } from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material/';
import SavingsIcon from '@mui/icons-material/Savings';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import toast, { Toaster } from 'react-hot-toast';

import { AppContext } from '../../../context/Context';
import { SocketContext } from '../../../context/SocketContext';
import { useTranslation } from 'react-i18next';
import '../../../assets/css/buttons.css';

import CashField from '../../AvslutteSkiftet/CashField';
import { formatSignedCash } from '../../../utils/formatCash';
import {
  verticallyCenteredLabelSx,
  fieldHelpSx,
} from '../../../theme/fieldTokens';
import { vendorTone, vendorPressSx } from '../../../theme/vendorTone';

// Flat tones, raised the same way the refusjon provider row is raised. Warm for
// the side that moves the drawer, cool for the side that leaves it alone —
// neither reuses a vendor colour, which means something else in this app
const ORIGIN_TONES = { till: '#8a4a00', outside: '#1f4f6f' };

const panelSx = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  px: 3,
  py: 2,
  borderRadius: '14px',
  backgroundColor: '#151d3b',
  border: '1px solid #2a3a6e',
};

const amountSx = {
  fontSize: '38px',
  fontWeight: 700,
  color: '#e6eaf2',
  fontVariantNumeric: 'tabular-nums',
};

function ModalSafe() {
  const { t } = useTranslation();
  const { socket } = useContext(SocketContext);

  const {
    hall,
    operator,
    session,
    updateShowModalSafe,
    updateIsLoadingScreen,
    updateFocusOnMainScreen,
  } = useContext(AppContext);

  const [miniSafe, setMiniSafe] = useState(null);
  const [safe, setSafe] = useState(null);

  // null → the two panels; otherwise the small form that action needs
  const [action, setAction] = useState(null);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [selectedBags, setSelectedBags] = useState([]);
  const [reference, setReference] = useState('');
  // null until the operator says it out loud; there is no sensible default —
  // assuming the till is what charged a drawer 116 000 kr it never held
  const [fromTill, setFromTill] = useState(null);
  const isBusy = useRef(false);

  //* DISABLE FOCUS ON MAIN SCREEN AND PREVENT ESCAPE BUTTON
  useEffect(() => {
    updateFocusOnMainScreen(false);

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      // Restored here rather than on each exit path, so no route out forgets
      updateFocusOnMainScreen(true);
    };
  }, []);

  const refresh = () => {
    socket.emit('miniSafeState', { bingoName: hall });
    socket.emit('safeState', { bingoName: hall });
  };

  useEffect(() => {
    if (hall) refresh();
  }, [hall]);

  useEffect(() => {
    const done = (label) => (data) => {
      updateIsLoadingScreen(false);
      isBusy.current = false;

      if (!data) {
        toast.error(t('error.error'));
        return;
      }
      if (data.ok === false) {
        const key =
          {
            amountRequired: 'safe.amountRequired',
            reasonRequired: 'safe.reasonRequired',
            insufficientBalance: 'safe.insufficientBalance',
            noBagsSelected: 'safe.noBagsSelected',
            bagNotFound: 'safe.bagNotFound',
            originRequired: 'safe.originRequired',
          }[data.reason] ?? 'error.error';
        toast.error(t(key));
        return;
      }

      toast.success(t(label));
      setAction(null);
      setAmount('');
      setReason('');
      setSelectedBags([]);
      setReference('');
      setFromTill(null);
      refresh();
    };

    const handleMini = (data) => data && setMiniSafe(data);
    const handleSafe = (data) => data && setSafe(data);

    const handlers = {
      miniSafeState: handleMini,
      safeState: handleSafe,
      miniSafeMove: done('safe.moveDone'),
      miniSafeCount: done('safe.countDone'),
      createBag: done('safe.bagCreated'),
      bankBags: done('safe.bagsBanked'),
      cancelBag: done('safe.bagCancelled'),
    };

    Object.entries(handlers).forEach(([e, h]) => socket.on(e, h));
    return () => Object.entries(handlers).forEach(([e, h]) => socket.off(e, h));
  }, [socket, hall]);

  const emit = (event, payload) => {
    if (isBusy.current) return;
    isBusy.current = true;
    updateIsLoadingScreen(true);
    socket.emit(event, {
      bingoName: hall,
      operator: operator,
      session: session,
      ...payload,
    });
  };

  const submitAction = () => {
    if (action === 'bank') {
      emit('bankBags', { bagIDs: selectedBags, reference });
      return;
    }
    if (amount === '' || Number(amount) < 0) {
      toast.error(t('safe.amountRequired'));
      return;
    }
    if (action === 'miniCount') {
      emit('miniSafeCount', { counted: Number(amount), reason });
      return;
    }
    // A count moves nothing, so it is the one action with no origin to give
    if (fromTill === null) {
      toast.error(t('safe.originRequired'));
      return;
    }
    if (action === 'newBag') {
      emit('createBag', { amount: Number(amount), fromTill });
      return;
    }
    if (!reason.trim()) {
      toast.error(t('safe.reasonRequired'));
      return;
    }
    emit('miniSafeMove', {
      direction: action === 'miniIn' ? 'in' : 'out',
      amount: Number(amount),
      reason,
      fromTill,
    });
  };

  const actionTile = (icon, label, onClick, tone) => (
    <button className={`btn-choice tone-${tone}`} onClick={onClick}>
      {icon}
      {label}
    </button>
  );

  const isCount = action === 'miniCount';

  const originHelpKey = (till, isOut) =>
    till
      ? isOut
        ? 'safe.originTillOutHelp'
        : 'safe.originTillHelp'
      : isOut
        ? 'safe.originOutsideOutHelp'
        : 'safe.originOutsideHelp';

  // Amber for the side that moves the drawer, blue for the side that does not:
  // the same reading the rest of the app gives those two tones
  const originTile = (till) => {
    const isOut = action === 'miniOut';
    const label = till
      ? isOut
        ? 'safe.originTillOut'
        : 'safe.originTill'
      : isOut
        ? 'safe.originOutsideOut'
        : 'safe.originOutside';
    const picked = fromTill === till;

    return (
      <Button
        variant="contained"
        onClick={() => setFromTill(till)}
        sx={{
          height: '70px',
          width: '230px',
          minWidth: '230px',
          padding: 0,
          borderRadius: '12px',
          color: 'white',
          fontWeight: 'bold',
          fontSize: '18px',
          textTransform: 'none',
          ...vendorTone(ORIGIN_TONES[till ? 'till' : 'outside']),
          ...vendorPressSx,
          // Both lit until one is chosen, so nothing looks preselected; after
          // that the unpicked one fades instead of hiding
          opacity: fromTill === null || picked ? 1 : 0.35,
          border: picked ? '3px solid #ffffff' : '3px solid transparent',
        }}
      >
        {t(label)}
      </Button>
    );
  };

  const ACTION_TITLES = {
    miniIn: 'safe.miniSafeIn',
    miniOut: 'safe.miniSafeOut',
    miniCount: 'safe.miniSafeCount',
    newBag: 'safe.newBag',
    bank: 'safe.toBank',
  };

  return (
    <div id="react-confirm-alert">
      <div className="react-confirm-alert-overlay">
        <div className="react-confirm-alert">
          <div
            className="react-confirm-alert-body modal-status"
            style={{
              width: '96vw',
              maxWidth: '1500px',
              maxHeight: '94vh',
              overflowY: 'auto',
            }}
          >
            {/* Inside this overlay, not outside it. ModalCashMovement has its own
                for the same reason: this modal is rendered from context, in its
                own stacking context, so a Toaster anywhere else paints its
                toasts behind it however high the z-index goes */}
            <Toaster
              position="top-center"
              reverseOrder={false}
              toastOptions={{ style: { fontSize: '24px' } }}
              containerStyle={{ zIndex: 99999999 }}
            />

            <h1>{t(action ? ACTION_TITLES[action] : 'safe.title')}</h1>

            {action === null ? (
              <>
                <Box sx={{ display: 'flex', gap: 3, mt: 2, textAlign: 'left' }}>
                  {/* Mini safe */}
                  <Box sx={panelSx}>
                    <Typography
                      sx={{ color: '#8fa3d4', fontSize: '17px', mb: 0.5 }}
                    >
                      {t('safe.miniSafe')}
                    </Typography>
                    <Box sx={amountSx}>
                      {formatSignedCash(miniSafe?.balance ?? 0)}
                      <span style={{ fontSize: '20px', color: '#8fa3d4' }}>
                        {' '}
                        kr
                      </span>
                    </Box>

                    {/* Most days it does not move, so "counted" is the signal */}
                    <Typography
                      sx={{
                        mt: 1,
                        fontSize: '15px',
                        color: miniSafe?.countedToday ? '#5ad19b' : '#fcad89',
                      }}
                    >
                      {miniSafe?.countedToday
                        ? t('safe.countedToday')
                        : t('safe.notCountedToday')}
                    </Typography>

                    <Box className="choice-row" sx={{ mt: 'auto', pt: 2 }}>
                      {actionTile(
                        <AddCircleIcon />,
                        t('safe.miniSafeIn'),
                        () => setAction('miniIn'),
                        'in'
                      )}
                      {actionTile(
                        <RemoveCircleIcon />,
                        t('safe.miniSafeOut'),
                        () => setAction('miniOut'),
                        'out'
                      )}
                      {actionTile(
                        <FactCheckIcon />,
                        t('safe.miniSafeCount'),
                        () => setAction('miniCount'),
                        'safe'
                      )}
                    </Box>
                  </Box>

                  {/* Safe */}
                  <Box sx={panelSx}>
                    <Typography
                      sx={{ color: '#8fa3d4', fontSize: '17px', mb: 0.5 }}
                    >
                      {t('safe.safe')}
                    </Typography>
                    <Box sx={amountSx}>
                      {formatSignedCash(safe?.total ?? 0)}
                      <span style={{ fontSize: '20px', color: '#8fa3d4' }}>
                        {' '}
                        kr
                      </span>
                    </Box>
                    <Typography sx={{ color: '#8fa3d4', fontSize: '15px' }}>
                      {t('safe.bagCount', { count: safe?.count ?? 0 })}
                    </Typography>

                    <Box
                      sx={{
                        mt: 1.5,
                        maxHeight: '30vh',
                        overflowY: 'auto',
                        pr: 1,
                      }}
                    >
                      {(safe?.bags ?? []).map((bag) => (
                        <Box
                          key={bag.id}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            py: 0.5,
                            borderBottom: '1px solid #2a3a6e',
                            color: '#c9d6f2',
                          }}
                        >
                          <Checkbox
                            checked={selectedBags.includes(bag.id)}
                            onChange={(e) =>
                              setSelectedBags((prev) =>
                                e.target.checked
                                  ? [...prev, bag.id]
                                  : prev.filter((id) => id !== bag.id)
                              )
                            }
                            sx={{
                              color: '#8fa3d4',
                              '&.Mui-checked': { color: '#5ad19b' },
                              '& .MuiSvgIcon-root': { fontSize: 26 },
                            }}
                          />
                          <span style={{ flex: 1 }}>
                            {bag.createdAt?.slice(0, 16)}
                            {bag.note ? ` — ${bag.note}` : ''}
                          </span>
                          <strong
                            style={{ fontVariantNumeric: 'tabular-nums' }}
                          >
                            {formatSignedCash(bag.amount)} kr
                          </strong>
                        </Box>
                      ))}
                      {!safe?.bags?.length && (
                        <Typography
                          sx={{ color: '#6e82b4', fontSize: '15px', py: 1 }}
                        >
                          {t('safe.noBags')}
                        </Typography>
                      )}
                    </Box>

                    <Box className="choice-row" sx={{ mt: 'auto', pt: 2 }}>
                      {actionTile(
                        <SavingsIcon />,
                        t('safe.newBag'),
                        () => setAction('newBag'),
                        'in'
                      )}
                      {actionTile(
                        <AccountBalanceIcon />,
                        t('safe.toBank'),
                        () => {
                          if (!selectedBags.length) {
                            toast.error(t('safe.noBagsSelected'));
                            return;
                          }
                          setAction('bank');
                        },
                        'out'
                      )}
                    </Box>
                  </Box>
                </Box>

                <div
                  className="react-confirm-alert-button-group"
                  style={{ marginTop: '28px' }}
                >
                  <button
                    className="btn-custom-no"
                    onClick={() => updateShowModalSafe(false)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                    }}
                  >
                    <CancelIcon style={{ fontSize: '1.2em' }} />
                    {t('sales.cancel')}
                  </button>
                </div>
              </>
            ) : (
              <>
                {/* The two-panel view needs the full 1500px; a form of two fields
                    does not. Without this the comment box stretches the whole
                    width of the modal and reads as a banner, not a field */}
                <Box sx={{ maxWidth: '620px', mx: 'auto', width: '100%' }}>
                  {action === 'bank' ? (
                    <>
                      {/* Nothing centres this step on its own: buttons.css only
                          reaches `> h1` and `> p` of the modal body, and the other
                          steps get it from the flex wrapper around their field */}
                      <Typography
                        sx={{
                          color: '#c9d6f2',
                          fontSize: '20px',
                          mt: 2,
                          textAlign: 'center',
                        }}
                      >
                        {t('safe.toBankSummary', {
                          count: selectedBags.length,
                          total: formatSignedCash(
                            (safe?.bags ?? [])
                              .filter((b) => selectedBags.includes(b.id))
                              .reduce((s, b) => s + Number(b.amount), 0)
                          ),
                        })}
                      </Typography>

                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          marginTop: '24px',
                        }}
                      >
                        <TextField
                          autoComplete="off"
                          label={t('safe.reference')}
                          value={reference}
                          onChange={(e) => setReference(e.target.value)}
                          variant="outlined"
                          inputProps={{ maxLength: 100 }}
                          InputLabelProps={{ sx: verticallyCenteredLabelSx }}
                          className="modal-textfield"
                          style={{ width: '340px' }}
                        />
                      </div>

                      <Typography sx={fieldHelpSx}>
                        {t('safe.referenceHelp')}
                      </Typography>
                    </>
                  ) : (
                    <>
                      {isCount && (
                        <Typography
                          sx={{
                            color: '#c9d6f2',
                            fontSize: '20px',
                            mt: 2,
                            textAlign: 'center',
                          }}
                        >
                          {t('safe.countQuestion')}
                        </Typography>
                      )}

                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          marginTop: '20px',
                        }}
                      >
                        <CashField
                          label={
                            t(isCount ? 'safe.countedLabel' : 'safe.amount') +
                            '*'
                          }
                          value={amount}
                          onChange={setAmount}
                          autoFocus
                        />
                      </div>

                      {/* The choice that decides whether the drawer is touched.
                        Neither side is preselected: this is the question the old
                        screen never asked, and a default is an answer nobody
                        reads. `miniOut` asks it the other way round — the money
                        is leaving the safe, so the question is where it goes */}
                      {action !== 'miniCount' && (
                        <Box sx={{ mt: 3 }}>
                          <Typography
                            sx={{
                              color: '#c9d6f2',
                              fontSize: '19px',
                              mb: 1.5,
                              textAlign: 'left',
                            }}
                          >
                            {t(
                              action === 'miniOut'
                                ? 'safe.originTitleOut'
                                : 'safe.originTitle'
                            )}
                          </Typography>

                          <Box
                            sx={{
                              display: 'flex',
                              gap: '10px',
                              justifyContent: 'center',
                            }}
                          >
                            {originTile(true)}
                            {originTile(false)}
                          </Box>

                          <Typography
                            sx={{ ...fieldHelpSx, minHeight: '22px' }}
                          >
                            {fromTill === null
                              ? ''
                              : t(
                                  originHelpKey(fromTill, action === 'miniOut')
                                )}
                          </Typography>
                        </Box>
                      )}

                      {/* A bag is only an amount: the note was never required and nobody filled it */}
                      {action !== 'newBag' && (
                        <>
                          <TextField
                            autoComplete="off"
                            // A count only needs an explanation when it disagrees,
                            // and the server is the one that knows. The asterisk
                            // used to be here anyway, promising a rule nothing enforced
                            label={
                              isCount
                                ? t('safe.countComment')
                                : t('safe.reason') + '*'
                            }
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            variant="outlined"
                            multiline
                            maxRows={4}
                            inputProps={{ maxLength: 500 }}
                            InputLabelProps={{ sx: verticallyCenteredLabelSx }}
                            className="modal-textfield"
                            style={{ width: '100%', marginTop: '24px' }}
                          />
                          <Typography sx={fieldHelpSx}>
                            {t(
                              isCount
                                ? 'safe.countCommentHelp'
                                : 'safe.reasonHelp'
                            )}
                          </Typography>
                        </>
                      )}
                    </>
                  )}
                </Box>

                <div className="react-confirm-alert-button-group">
                  <button
                    className="btn-custom-yes"
                    onClick={submitAction}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                    }}
                  >
                    <CheckCircleIcon style={{ fontSize: '1.2em' }} />
                    {t('sales.confirm')}
                  </button>
                  <button
                    className="btn-custom-no"
                    onClick={() => {
                      setAction(null);
                      setAmount('');
                      setReason('');
                      setFromTill(null);
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                    }}
                  >
                    <ArrowBackIcon style={{ fontSize: '1.2em' }} />
                    {t('sales.openShiftBack')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ModalSafe;
